import type { BlockNode, InlineNode, RichTextDoc } from './schema';

/**
 * `messages.plain_text` (PLAN §3.1 "Message body storage"): the text that notifications, email,
 * AI prompts, keyword matching and search read. Mentions become `@Display Name`, channel links
 * `#name`, emoji `:name:`, links their visible text followed by the URL when they differ, code
 * blocks their contents. It records what was sent: a later rename does not rewrite it (renderers
 * use the ids in `content_json`).
 *
 * The result is normalised for search (D-050): NFC, zero-width characters removed. Devanagari and
 * Hinglish text pass through unchanged apart from that.
 */

export interface PlainTextResolver {
  /** Display name of a mentioned user (without `@`). */
  userName?(userId: string): string | null | undefined;
  /** Handle of a mentioned user group (Phase 2, without `@`). */
  groupHandle?(groupId: string): string | null | undefined;
  /**
   * Name of a linked conversation (without `#`). Return a neutral placeholder such as
   * `private-channel` for channels the message's readers may not know about.
   */
  channelName?(conversationId: string): string | null | undefined;
}

/** Zero-width characters removed for search (D-050): ZWSP, ZWNJ, ZWJ, word joiner, BOM. */
const ZERO_WIDTH = /[\u200B-\u200D\u2060\uFEFF]/g;

/** The search normalisation of D-050, applied to plain text before it is stored. */
export function normalizeSearchText(value: string): string {
  return value.normalize('NFC').replace(ZERO_WIDTH, '');
}

function inlineText(nodes: readonly InlineNode[], resolver: PlainTextResolver): string {
  let out = '';
  let i = 0;
  while (i < nodes.length) {
    const node = nodes[i]!;
    if (node.type === 'text') {
      const link = node.marks?.find((mark) => mark.type === 'link');
      if (link && link.type === 'link') {
        // A link may span several text nodes (bold part of the label, …): join them first.
        let label = '';
        while (i < nodes.length) {
          const next = nodes[i]!;
          const nextLink = next.type === 'text' ? next.marks?.find((m) => m.type === 'link') : null;
          if (!nextLink || nextLink.type !== 'link' || nextLink.attrs.href !== link.attrs.href) {
            break;
          }
          label += (next as { text: string }).text;
          i++;
        }
        const href = link.attrs.href;
        const bare = href.replace(/^mailto:/i, '');
        out += label.trim() === href || label.trim() === bare ? label : `${label} (${href})`;
        continue;
      }
      out += node.text;
    } else if (node.type === 'hardBreak') {
      out += '\n';
    } else if (node.type === 'mention') {
      const { kind, id, label } = node.attrs;
      if (kind === 'here') out += '@here';
      else if (kind === 'channel') out += '@channel';
      else if (kind === 'user') out += `@${resolver.userName?.(id) ?? label ?? 'unknown'}`;
      else out += `@${resolver.groupHandle?.(id) ?? label ?? 'group'}`;
    } else if (node.type === 'channelLink') {
      out += `#${resolver.channelName?.(node.attrs.id) ?? node.attrs.label ?? 'channel'}`;
    } else {
      out += `:${node.attrs.name}:`;
    }
    i++;
  }
  return out;
}

function prefixLines(text: string, first: string, rest: string): string {
  return text
    .split('\n')
    .map((line, index) => (index === 0 ? first : rest) + line)
    .join('\n');
}

function blockText(block: BlockNode, resolver: PlainTextResolver): string {
  switch (block.type) {
    case 'paragraph':
      return inlineText(block.content ?? [], resolver);
    case 'codeBlock':
      return (block.content ?? []).map((node) => node.text).join('');
    case 'blockquote':
      return prefixLines(blocksText(block.content, resolver), '> ', '> ');
    case 'bulletList':
      return block.content
        .map((item) => prefixLines(blocksText(item.content, resolver), '- ', '  '))
        .join('\n');
    case 'orderedList': {
      const start = block.attrs?.start ?? 1;
      return block.content
        .map((item, index) => {
          const marker = `${start + index}. `;
          return prefixLines(blocksText(item.content, resolver), marker, ' '.repeat(marker.length));
        })
        .join('\n');
    }
  }
}

function blocksText(blocks: readonly BlockNode[], resolver: PlainTextResolver): string {
  return blocks.map((block) => blockText(block, resolver)).join('\n');
}

/** Derives the plain-text projection of a (validated) document. */
export function toPlainText(doc: RichTextDoc, resolver: PlainTextResolver = {}): string {
  const text = blocksText(doc.content, resolver)
    .split('\n')
    .map((line) => line.replace(/[ \t]+$/, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return normalizeSearchText(text);
}

let graphemes: Intl.Segmenter | undefined;

/**
 * At most `max` UTF-16 units of plain text on one line, for previews and notifications. Cuts only
 * between grapheme clusters, so a Devanagari syllable or an emoji sequence is never split.
 */
export function previewText(plainText: string, max = 140): string {
  const flat = plainText.replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;
  graphemes ??= new Intl.Segmenter(undefined, { granularity: 'grapheme' });
  let out = '';
  for (const { segment } of graphemes.segment(flat)) {
    if (out.length + segment.length > max - 1) break;
    out += segment;
  }
  return `${out.trimEnd()}…`;
}
